import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';

export interface ResourceSeries {
  id: string;
  title: string;
  description: string | null;
  cover_art_url: string | null;
  tags: string[];
  book_of_bible: string | null;
  topic: string | null;
  week_count: number;
  published: boolean;
  sort_order: number;
  created_at: string;
}

export interface SeriesFilters {
  keyword: string;
  books: string[];
  topics: string[];
  weekMin: number;
  weekMax: number;
}

const DEFAULT_FILTERS: SeriesFilters = {
  keyword: '',
  books: [],
  topics: [],
  weekMin: 1,
  weekMax: 52,
};

function filtersFromParams(params: URLSearchParams): SeriesFilters {
  return {
    keyword: params.get('q') ?? '',
    books: params.getAll('book'),
    topics: params.getAll('topic'),
    weekMin: parseInt(params.get('weekMin') ?? '1'),
    weekMax: parseInt(params.get('weekMax') ?? '52'),
  };
}

function paramsFromFilters(filters: SeriesFilters): URLSearchParams {
  const p = new URLSearchParams();
  if (filters.keyword) p.set('q', filters.keyword);
  filters.books.forEach(b => p.append('book', b));
  filters.topics.forEach(t => p.append('topic', t));
  if (filters.weekMin !== 1) p.set('weekMin', String(filters.weekMin));
  if (filters.weekMax !== 52) p.set('weekMax', String(filters.weekMax));
  return p;
}

export function useResourceSeries() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [series, setSeries] = useState<ResourceSeries[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const [availableBooks, setAvailableBooks] = useState<string[]>([]);

  const filters = filtersFromParams(searchParams);

  const setFilters = useCallback((next: Partial<SeriesFilters>) => {
    setSearchParams(paramsFromFilters({ ...filters, ...next }), { replace: true });
  }, [filters, setSearchParams]);

  // One-time fetch of all published series to derive available filter options
  useEffect(() => {
    let mounted = true;
    supabase
      .from('resource_series')
      .select('tags, book_of_bible')
      .eq('published', true)
      .then(({ data }) => {
        if (!mounted || !data) return;
        const tags = new Set<string>();
        const books = new Set<string>();
        for (const s of data) {
          (s.tags as string[]).forEach(t => tags.add(t));
          if (s.book_of_bible) books.add(s.book_of_bible as string);
        }
        setAvailableTags([...tags].sort());
        setAvailableBooks([...books].sort());
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        let query = supabase
          .from('resource_series')
          .select('*')
          .eq('published', true)
          .order('sort_order', { ascending: true })
          .order('created_at', { ascending: false });

        if (filters.books.length > 0) {
          query = query.in('book_of_bible', filters.books);
        }
        if (filters.topics.length > 0) {
          query = query.in('topic', filters.topics);
        }
        if (filters.weekMin > 1) {
          query = query.gte('week_count', filters.weekMin);
        }
        if (filters.weekMax < 52) {
          query = query.lte('week_count', filters.weekMax);
        }

        const { data, error: dbError } = await query;
        if (!mounted) return;
        if (dbError) throw dbError;

        let results = data as ResourceSeries[];
        if (filters.keyword) {
          const kw = filters.keyword.toLowerCase();
          results = results.filter(s =>
            s.title.toLowerCase().includes(kw) ||
            s.description?.toLowerCase().includes(kw) ||
            s.tags.some(t => t.toLowerCase().includes(kw))
          );
        }
        setSeries(results);
      } catch (err) {
        if (mounted) setError(err instanceof Error ? err.message : 'Failed to load series');
      } finally {
        if (mounted) setLoading(false);
      }
    };

    load();
    return () => { mounted = false; };
  }, [searchParams]);

  return { series, loading, error, filters, setFilters, availableTags, availableBooks };
}
